import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MessageSquare } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { DESTINACAO_LABELS } from "@/lib/constants";
import {
  afirmacoesHabilitadas,
  destinacaoSugeridaParceiro,
  respostasConformeDestinacao,
} from "@shared/laudo-checklist";
import type { LaudoPecaRespostaWithPergunta } from "@shared/schema";
import { api } from "@shared/routes";

// Inexistente vem separada das demais: não é um destino, indica que a peça não existe.
const DESTINOS = ["venda_imediata", "recuperacao", "descarte"];
const PARCIAL =
  "A destinação foi salva, mas as respostas não. Tente salvar novamente.";

interface PreencherRespostasProps {
  laudoPecaId: string;
  nomePeca: string;
  respostas: LaudoPecaRespostaWithPergunta[];
  /** Destinação gravada na peça. */
  destinacao: string | null;
  fotoUrl: string | null;
  moduloDetran: boolean;
  podeEditarDestinacao: boolean;
  onSaved: () => void;
}

/** Inexistente desmarca todas as afirmações (o banco também força false). */
const conforme = (marcadas: Record<string, boolean>, destinacao: string | null) =>
  Object.fromEntries(
    respostasConformeDestinacao(
      Object.entries(marcadas).map(([id, resposta]) => ({ id, resposta })),
      destinacao,
    ).map((r) => [r.id, r.resposta]),
  );

export function PreencherRespostas({
  laudoPecaId,
  nomePeca,
  respostas,
  destinacao: destinacaoGravada,
  fotoUrl,
  moduloDetran,
  podeEditarDestinacao,
  onSaved,
}: PreencherRespostasProps) {
  const { session } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [destinacao, setDestinacao] = useState<string | null>(null);
  // Última destinação que o servidor confirmou: o próximo Salvar só reenvia o que ainda falta.
  const [confirmada, setConfirmada] = useState<string | null>(null);
  const [sugeridaSemFoto, setSugeridaSemFoto] = useState(false);
  const [destinacaoSalva, setDestinacaoSalva] = useState(false);
  const [localRespostas, setLocalRespostas] = useState<Record<string, boolean>>({});
  const [observacoes, setObservacoes] = useState<Record<string, string | null>>({});
  const [erro, setErro] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Ao abrir, recarrega o que está gravado (o que ficou pela metade numa abertura anterior não volta).
  const reiniciar = () => {
    const inicial = podeEditarDestinacao
      ? destinacaoSugeridaParceiro({ destinacao: destinacaoGravada, fotoUrl }, moduloDetran)
      : destinacaoGravada;
    setDestinacao(inicial);
    setConfirmada(destinacaoGravada);
    setSugeridaSemFoto(podeEditarDestinacao && !destinacaoGravada && inicial === "inexistente");
    setDestinacaoSalva(false);
    setLocalRespostas(conforme(Object.fromEntries(respostas.map((r) => [r.id, r.resposta])), inicial));
    setObservacoes(Object.fromEntries(respostas.map((r) => [r.id, r.observacoes])));
    setErro(null);
  };

  const handleOpenChange = (aberto: boolean) => {
    if (aberto) reiniciar();
    setOpen(aberto);
  };

  const mudarDestinacao = (valor: string) => {
    setDestinacao(valor);
    setSugeridaSemFoto(false);
    setLocalRespostas((prev) => conforme(prev, valor));
  };

  const enviar = async (url: string, corpo: unknown, padrao: string) => {
    const res = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session?.access_token}`,
      },
      body: JSON.stringify(corpo),
    }).catch(() => null); // sem rede: mensagem em português em vez do texto técnico do navegador
    if (!res) throw new Error(padrao);
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.message || padrao);
    }
  };

  const mudouDestinacao = podeEditarDestinacao && !!destinacao && destinacao !== confirmada;

  const handleSave = async () => {
    setIsSaving(true);
    setErro(null);
    let etapa: "destinacao" | "respostas" = "destinacao";
    let persistiu = false;
    try {
      if (mudouDestinacao) {
        await enviar(
          api.laudoPecas.update.path.replace(":id", laudoPecaId),
          { destinacao },
          "Não foi possível salvar a destinação.",
        );
        setConfirmada(destinacao);
        setDestinacaoSalva(true);
        persistiu = true;
      }
      etapa = "respostas";
      if (respostas.length > 0) {
        await enviar(
          api.laudoPecas.updateRespostas.path.replace(":id", laudoPecaId),
          {
            respostas: respostas.map((r) => ({
              id: r.id,
              resposta: !!localRespostas[r.id],
              observacoes: observacoes[r.id] ?? r.observacoes ?? "",
            })),
          },
          "Não foi possível salvar as respostas.",
        );
        persistiu = true;
      }
      toast({ title: "Checklist da peça salvo." });
      setOpen(false);
    } catch (err) {
      const mensagem = err instanceof Error ? err.message : "Tente novamente.";
      setErro(etapa === "respostas" && (persistiu || destinacaoSalva) ? PARCIAL : mensagem);
      toast({ title: "Erro ao salvar", description: mensagem, variant: "destructive" });
    } finally {
      if (persistiu) onSaved(); // a tela atrás do diálogo reflete o que já foi salvo
      setIsSaving(false);
    }
  };

  const respondidas = respostas.filter((r) => r.resposta).length;
  const habilitadas = afirmacoesHabilitadas(destinacao);
  const nadaASalvar = respostas.length === 0 && !mudouDestinacao;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          <MessageSquare className="w-3.5 h-3.5" />
          Responder
          {respostas.length > 0 && (
            <Badge variant="secondary" className="ml-1 text-xs px-1.5 py-0">
              {respondidas}/{respostas.length}
            </Badge>
          )}
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Questões de análise da peça — {nomePeca}</DialogTitle>
          <DialogDescription>
            {podeEditarDestinacao
              ? "Sugira a destinação e responda às questões de análise da peça."
              : "Responda às questões de análise da peça."}
          </DialogDescription>
        </DialogHeader>

        {/* A destinação vem antes porque é ela que trava ou libera as afirmações. */}
        {podeEditarDestinacao ? (
          <div className="space-y-1.5">
            <Label htmlFor={`destinacao-${laudoPecaId}`}>Destinação sugerida</Label>
            <Select value={destinacao ?? ""} onValueChange={mudarDestinacao} disabled={isSaving}>
              <SelectTrigger id={`destinacao-${laudoPecaId}`}>
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                {DESTINOS.map((chave) => (
                  <SelectItem key={chave} value={chave}>
                    {DESTINACAO_LABELS[chave]}
                  </SelectItem>
                ))}
                <SelectSeparator />
                <SelectItem value="inexistente">{DESTINACAO_LABELS.inexistente}</SelectItem>
              </SelectContent>
            </Select>
            {destinacao === "inexistente" && (
              <div className="space-y-0.5 text-xs text-muted-foreground">
                {sugeridaSemFoto && <p>Sugerida como inexistente porque a peça ficou sem foto.</p>}
                <p>Peça inexistente: as respostas ficam desmarcadas. Para responder, mude a destinação.</p>
              </div>
            )}
          </div>
        ) : (
          destinacao && (
            <div className="space-y-0.5">
              <p className="text-sm">Destinação sugerida: {DESTINACAO_LABELS[destinacao]}</p>
              {destinacao === "inexistente" && (
                <p className="text-xs text-muted-foreground">Peça marcada como inexistente.</p>
              )}
            </div>
          )
        )}

        {respostas.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            Esta peça não tem questões de análise cadastradas.
          </p>
        ) : (
          <div className="space-y-1">
            {respostas.map((r, i) => {
              const marcada = !!localRespostas[r.id];
              return (
                <div key={r.id} className="space-y-2 pb-2 border-b last:border-b-0">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id={`resposta-${r.id}`}
                      className="mt-3.5"
                      checked={marcada}
                      disabled={!habilitadas || isSaving}
                      onCheckedChange={(valor) =>
                        setLocalRespostas((prev) => ({ ...prev, [r.id]: valor === true }))
                      }
                    />
                    <Label
                      htmlFor={`resposta-${r.id}`}
                      className="flex min-h-11 flex-1 cursor-pointer items-center text-sm font-medium leading-snug text-slate-700"
                    >
                      <span aria-hidden className="mr-1.5 text-slate-400">{i + 1}.</span>
                      {r.pergunta?.texto}
                    </Label>
                  </div>
                  {marcada && (
                    <Textarea
                      aria-label={`Observação da questão ${i + 1}`}
                      value={observacoes[r.id] ?? ""}
                      onChange={(e) => setObservacoes((prev) => ({ ...prev, [r.id]: e.target.value }))}
                      placeholder="Observação…"
                      rows={2}
                      className="resize-none text-xs"
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {erro && (
          <p role="alert" className="text-sm text-destructive">
            {erro}
          </p>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={() => setOpen(false)} disabled={isSaving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isSaving || nadaASalvar}>
            {isSaving ? "Salvando…" : "Salvar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
